import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";

export interface IMediaItem {
  id: string;
  type: "image" | "video";
  url: string;
}

export interface IAuthorData {
  username: string;
  avatar?: string;
  user_rating?: {
    score?: number;
  };
  created_at?: string;
}
export interface IPost {
  id: string;
  title: string;
  content: string;
  author: string;
  authorData: IAuthorData;
  created_at: string;
  last_activity_at: string;
  media?: string[];
  comments?: { count: number }[];
  is_pinned: boolean;
  is_hidden?: boolean;
}

export interface ICreatePost {
  title: string;
  content: string;
  author: string;
  media?: IMediaItem[] | string;
}

export async function createPost(post: ICreatePost): Promise<IPost | false> {
  const { data, error } = await supabase
    .from("posts")
    .insert({
      ...post,
    })
    .select("*")
    .single();

  if (error) {
    console.error("Error post creation", error);
    return false;
  }

  return data;
}

export async function fetchPosts({
  pageParam = 0,
  pageSize = 12,
  search,
}: {
  pageParam?: number;
  pageSize?: number;
  search?: string;
}): Promise<IPost[]> {
  let query = supabase
    .from("posts")
    .select(
      `
      *,
      authorData:user_subscriptions (
        username,
        avatar,
				created_at,
        user_rating (
          score
        )
      ),
      comments(count)
    `
    )
    .eq("is_hidden", false)
    .eq("comments.is_deleted", false)
    .order("is_pinned", { ascending: false })
    .order("pin_priority", { ascending: false })
    .order("created_at", { ascending: false })
    .order("last_activity_at", { ascending: false })
    .range(pageParam, pageParam + pageSize - 1);

  if (search && search.trim()) {
    query = query.or(`title.ilike.%${search}%,content.ilike.%${search}%`);
  }

  const { data, error } = await query;

  if (error) return [];
  return data as IPost[];
}

export async function fetchPostsByCreatedTime({
  pageParam = 0,
  pageSize = 12,
  search,
  isDeleted,
}: {
  pageParam?: number;
  pageSize?: number;
  search?: string;
  isDeleted?: boolean;
}): Promise<IPost[]> {
  let query = supabase
    .from("posts")
    .select(
      `
      *,
      authorData:user_subscriptions (
        username,
        avatar,
				created_at,
        user_rating (
          score
        )
      ),
      comments(count)
    `
    )

    .order("created_at", { ascending: false })
    .range(pageParam, pageParam + pageSize - 1);

  if (isDeleted) {
    query = query.eq("is_hidden", true);
  } else {
    query = query.eq("is_hidden", false);
  }
  if (search && search.trim()) {
    query = query.or(`title.ilike.%${search}%,content.ilike.%${search}%`);
  }

  const { data, error } = await query;

  if (error) return [];
  return data as IPost[];
}

export async function updatePost(
  post: Partial<IPost> & { id: string }
): Promise<IPost | false> {
  const { data, error } = await supabase
    .from("posts")
    .update({
      title: post.title,
      content: post.content,
      media: post.media,
    })
    .eq("id", post.id)
    .select(
      `
      *,
      authorData:user_subscriptions (
        username,
        avatar,
				created_at,
        user_rating (
          score
        )
      ),
      comments(count)
      `
    )
    .single();
  if (error) {
    console.error("Error updating post", error);
    return false;
  }

  return data;
}

export async function fetchPostById(
  id: string | number
): Promise<IPost | null> {
  const { data, error } = await supabase
    .from("posts")
    .select(
      `
      *,
      authorData:user_subscriptions (
        username,
        avatar,
        user_rating (
          score
        )
      ),
      comments(count)
    `
    )
    .eq("id", id)
    .eq("is_hidden", false)
    .single();

  if (error) return null;
  return data as IPost;
}

export async function fetchPostsByUserId({
  userId,
  pageParam = 0,
  pageSize = 12,
}: {
  userId: string;
  pageParam?: number;
  pageSize?: number;
}): Promise<IPost[]> {
  const { data, error } = await supabase
    .from("posts")
    .select(
      `
      *,
      authorData:user_subscriptions (
        username,
				avatar,
				user_rating (
					score
				)
      ),
      comments(count)
    `
    )
    .eq("author", userId)
    .eq("is_hidden", false)
    .order("last_activity_at", { ascending: false })
    .order("created_at", { ascending: false })
    .range(pageParam, pageParam + pageSize - 1);

  if (error) return [];
  return data as IPost[];
}

export async function fetchUserPostsActivity({
  userId,
}: {
  userId: string;
}): Promise<{ created_at: string }[]> {
  const currentYear = new Date().getFullYear();
  const startDate = `${currentYear}-01-01T00:00:00Z`;
  const endDate = `${currentYear + 1}-01-01T00:00:00Z`;

  const { data, error } = await supabase
    .from("posts")
    .select("created_at")
    .eq("author", userId)
    .gte("created_at", startDate)
    .lt("created_at", endDate);

  if (error) return [];
  return data as { created_at: string }[];
}

export async function uploadImageToStorage(file: File): Promise<string | null> {
  try {
    const fileExt = file.name.split(".").pop();
    const fileName = `${uuidv4()}.${fileExt}`;
    const filePath = `uploads/${fileName}`;

    const { error } = await supabase.storage
      .from("community")
      .upload(filePath, file);

    if (error) {
      console.error("Image upload error:", error.message);
      return null;
    }

    const { data: publicData } = supabase.storage
      .from("community")
      .getPublicUrl(filePath);

    return publicData?.publicUrl || null;
  } catch (err) {
    console.error("Unexpected upload error:", err);
    return null;
  }
}

export const fetchDeletePost = async (commentId: string) => {
  const { data, error } = await supabase
    .from("comments")
    .update({
      is_deleted: true,
    })
    .eq("id", commentId);

  return { data, error };
};

// COMMENTS

export interface IComment {
  id: string;
  content: string;
  author: string;
  media?: string[];
  post_id: number | string;
  parent_id?: number | string;
  authorData: IAuthorData;
  is_deleted: boolean;
  created_at: string;
}

interface ICreateComment {
  content: string;
  author: string;
  media?: IMediaItem[] | string;
  post_id: number | string;
  parent_id?: number | string;
}

export async function createComment(
  comment: ICreateComment
): Promise<IComment | false> {
  const { data, error } = await supabase
    .from("comments")
    .insert({
      ...comment,
    })
    .select("*")
    .single();

  if (error) {
    console.error("Error post creation", error);
    return false;
  }

  return data;
}

export const fetchDeleteComment = async (commentId: string) => {
  const { data, error } = await supabase
    .from("comments")
    .update({
      is_deleted: true,
    })
    .eq("id", commentId);

  return { data, error };
};
export const fetchCommentsCount = async (postId, parentId) => {
  if (!postId || !parentId) return 0;
  const { count } = await supabase
    .from("comments")
    .select("id", { count: "exact", head: true })
    .eq("post_id", postId)
    .eq("parent_id", parentId)
    .eq("is_deleted", false);
  return count || 0;
};

export const fetchComments = async (postId, parentId: string | null) => {
  if (!postId) return [];
  let query = supabase
    .from("comments")
    .select(
      `
      *,
      authorData:user_subscriptions (
        user_id,
        username,
				avatar,
        created_at,
				user_rating (
					score
				)
      )
    `
    )
    .eq("post_id", postId)
    .eq("is_deleted", false);
  if (parentId) {
    query = query.eq("parent_id", parentId);
  } else {
    query = query.is("parent_id", parentId);
  }
  query = query.order("created_at", { ascending: true });
  const { data } = await query;
  return data || [];
};

// Likes

export interface ILike {
  parent_id: number | string;
  user_id: string;
}
export type TParentType = "post" | "comment";

export async function fetchIsLiked(
  parentId: number | string,
  userId: string,
  parentType: TParentType
): Promise<boolean> {
  if (!parentId || !userId) return false;
  const table = parentType === "post" ? "post_likes" : "comment_likes";
  const { data } = await supabase
    .from(table)
    .select("user_id")
    .eq("parent_id", parentId)
    .eq("user_id", userId)
    .maybeSingle();
  return !!data;
}

export async function fetchLikeCount(
  parentId: number | string,
  parentType: TParentType
): Promise<number> {
  if (!parentId) return 0;
  const table = parentType === "post" ? "post_likes" : "comment_likes";
  const { count } = await supabase
    .from(table)
    .select("*", { count: "exact", head: true })
    .eq("parent_id", parentId);
  return count ?? 0;
}

export async function like(
  parentId: number | string,
  userId: string,
  parentType: TParentType
): Promise<void> {
  const table = parentType === "post" ? "post_likes" : "comment_likes";
  await supabase.from(table).insert({ parent_id: parentId, user_id: userId });
}

export async function unlike(
  parentId: number | string,
  userId: string,
  parentType: TParentType
): Promise<void> {
  const table = parentType === "post" ? "post_likes" : "comment_likes";
  await supabase
    .from(table)
    .delete()
    .match({ parent_id: parentId, user_id: userId });
}

// User Rating

export interface ILeader {
  user_id: string;
  score: number;
  total_score?: number; // Total score for the user, used in weekly/monthly ratings
  username: string;
  avatar?: string;
}

export async function fetchTotalRating() {
  const { data, error } = await supabase
    .from("user_rating")
    .select("user_id,score,user_subscriptions(username, avatar)")
    .order("score", { ascending: false })
    .limit(10);
  if (error) throw error;
  return (data as any[]).map((item) => ({
    user_id: item.user_id,
    score: item.score,
    username: item.user_subscriptions?.username ?? "unknown",
    avatar: item.user_subscriptions?.avatar ?? null,
  })) as ILeader[];
}

export async function fetchWeeklyRating(): Promise<ILeader[]> {
  const { data, error } = await supabase.rpc("get_weekly_user_rating");
  if (error) throw error;
  if (!data || !data.length) return [];
  const userIds = data.map((u: any) => u.user_id);
  const { data: users, error: usersError } = await supabase
    .from("user_subscriptions")
    .select("user_id, username, avatar")
    .in("user_id", userIds);
  if (usersError) throw usersError;

  const results = await Promise.all(
    data.map(async (item: any) => {
      const user = users?.find((u: any) => u.user_id === item.user_id);
      const userWithTotal = await supabase
        .from("user_rating")
        .select("score")
        .eq("user_id", user.user_id)
        .single();
      return {
        user_id: item.user_id,
        score: item.week_score,
        total_score: userWithTotal.data?.score ?? 0,
        username: user?.username ?? "unknown",
        avatar: user?.avatar ?? null,
      };
    })
  );

  return results.filter(el => el.score >= 0);
}

export async function fetchMonthlyRating(): Promise<ILeader[]> {
  const { data, error } = await supabase.rpc("get_monthly_user_rating");
  if (error) throw error;
  if (!data || !data.length) return [];
  const userIds = data.map((u: any) => u.user_id);
  const { data: users, error: usersError } = await supabase
    .from("user_subscriptions")
    .select("user_id, username, avatar")
    .in("user_id", userIds);
  if (usersError) throw usersError;
  const results = await Promise.all(
    data.map(async (item: any) => {
      const user = users?.find((u: any) => u.user_id === item.user_id);
      const userWithTotal = await supabase
        .from("user_rating")
        .select("score")
        .eq("user_id", user.user_id)
        .single();
      return {
        user_id: item.user_id,
        score: item.month_score,
        total_score: userWithTotal.data?.score ?? 0,
        username: user?.username ?? "unknown",
        avatar: user?.avatar ?? null,
      };
    })
  );

  return results.filter(el => el.score >= 0);
}

export async function fetchUsersByLevel() {
  const { data, error } = await supabase.rpc("get_users_by_level");
  if (error) throw error;
  return data as { level: number; users_count: number }[];
}

export async function deletePost(id: string) {
  try {
    const { error } = await supabase
      .from("posts")
      .update({ is_hidden: true })
      .eq("id", id);

    if (error) {
      return;
    }
  } catch (error) {
    console.error("Error occured:", error);
  }
}

// Moderator survey

export type AdminPanelComment = {
  id: string;
  content: string;
  created_at: string;
  updated_at: string;
  parent_id: string | null;
  user_sub_id: string;
  is_deleted: boolean;
  authorData: {
    user_id: string;
    username: string;
    avatar: string | null;
    created_at: string;
    user_rating: {
      score: number | null;
    } | null;
  };
  media?: string[];
  parent?: AdminPanelComment | null;
  post_id: string;
};

export const fetchCommentsQuery = async ({
  pageParam = 0,
  pageSize = 12,
  search,
  querySearch,
}: {
  pageParam?: number;
  pageSize?: number;
  search?: string;
  querySearch?: {
    isDeleted?: boolean;
    authorId?: string;
  };
}): Promise<AdminPanelComment[]> => {
  let query = supabase
    .from("comments")
    .select(
      `
    *,
    parent:parent_id (
      *,
      authorData:user_subscriptions (
        user_id,
        username,
        avatar,
        created_at,
        user_rating (
          score
        )
      )
    ),
    authorData:user_subscriptions (
      user_id,
      username,
      avatar,
      created_at,
      user_rating (
        score
      )
    )
  `
    )
    .range(pageParam, pageParam + pageSize - 1);

  if (search && search.trim()) {
    query = query.or(`content.ilike.%${search}%`);
  }

  if (querySearch?.isDeleted) {
    query = query.eq("is_deleted", true);
  } else {
    query = query.eq("is_deleted", false);
  }

  query = query.order("created_at", { ascending: true });
  const { data } = await query;
  return data || [];
};
